/**
 * What a modal dialog does to the page around it, in one place so no two dialogs can differ in it. Every
 * dialog in the app reaches it through `Dialog` (`./Dialog.tsx`); it was written for the consent screens and
 * moved here when the other dialogs adopted it (Apple design review X-8).
 *
 * `aria-modal="true"` asks a screen reader to ignore the page behind a dialog, but not every one does,
 * and a keyboard's Tab can leave a dialog that only traps the key at its two ends. So while a dialog is
 * open **everything else in `<body>` is `inert`**: unreachable by Tab, by a click, by find-in-page and by
 * a screen reader's cursor, which is what "nothing is reachable behind an open dialog" has to mean. A
 * dialog stacked on another (the full text over the ask) makes the lower one inert too, and gives it back
 * when it leaves.
 *
 * Three more things belong to the same moment and are done here, once per opening:
 *
 * - **The opener is remembered before anything else is touched**, because making the page inert blurs
 *   the control that had focus, and focus goes back to it when the dialog leaves (the pill someone
 *   pressed lands where it was). A caller that knows its opener names it (`returnFocusRef`): Safari does
 *   not focus a button on a click, so what has focus then is `<body>`. If the opener is gone by then the
 *   caller decides where focus goes.
 * - **The page behind does not scroll** (`overflow: hidden` on `<body>`, and put back as it was).
 * - **Focus goes in**: to `initialFocusRef`, so a screen reader announces the dialog by its label.
 */

import { useEffect, type RefObject } from 'react'

/** What Tab may land on inside a dialog. An `aria-disabled` button stays here on purpose: it keeps focus. */
const FOCUSABLE = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

export function useModal(
  open: boolean,
  hostRef: RefObject<HTMLElement | null>,
  initialFocusRef: RefObject<HTMLElement | null>,
  returnFocusRef?: RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    if (!open) return
    const opener = returnFocusRef?.current ?? document.activeElement
    const host = hostRef.current

    // Inert everything that is not this dialog, except what is already inert (the page, made so by the
    // dialog this one is stacked on): that one gives its own back.
    const madeInert: Element[] = []
    for (const child of Array.from(document.body.children)) {
      if (child === host || child.hasAttribute('inert')) continue
      child.setAttribute('inert', '')
      madeInert.push(child)
    }
    initialFocusRef.current?.focus()
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
      // Inert goes first: focus cannot be given to something inside an inert subtree.
      for (const child of madeInert) child.removeAttribute('inert')
      // Focus goes back to the opener only if it is still the dialog's to give: on `<body>` (making the
      // page inert blurred what had it) or inside the dialog. A caller that has already put it somewhere
      // else (the heading a removed note leaves it on) keeps it: taking it back to a control that is about
      // to go dropped the keyboard on `<body>`, when this ran a frame after the caller's own move.
      const active = document.activeElement
      const stillOurs = !active || active === document.body || (host?.contains(active) ?? false)
      if (stillOurs && opener instanceof HTMLElement && opener.isConnected) opener.focus()
    }
  }, [open, hostRef, initialFocusRef, returnFocusRef])
}

/**
 * The keys of a modal dialog, while `active` (open, and not paused for a dialog stacked above it): Escape
 * calls `onEscape`, and Tab and Shift+Tab stay inside `panel`. One place, so the dialog shell and the full
 * text cannot differ in what the keyboard does.
 *
 * Bound on the document, not on the panel: focus can be on `<body>` after a click on the scrim's blank.
 */
export function useModalKeys(active: boolean, panelRef: RefObject<HTMLElement | null>, onEscape: () => void) {
  useEffect(() => {
    if (!active) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onEscape()
        return
      }
      trapTab(event, panelRef.current)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [active, panelRef, onEscape])
}

/**
 * Tab and Shift+Tab stay inside `panel`: from the last control to the first and the other way round,
 * and from anywhere outside it (focus can be on `<body>` after a click on the scrim's blank) back in.
 * Focus on something inside that Tab cannot land on (the title a confirmation opens on, so a screen
 * reader starts at the question) goes to the first control on Tab and the last on Shift+Tab: from the
 * title, the browser's own Shift+Tab left the dialog.
 */
function trapTab(event: KeyboardEvent, panel: HTMLElement | null) {
  if (event.key !== 'Tab' || !panel) return
  const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE))
  if (focusables.length === 0) {
    event.preventDefault()
    panel.focus()
    return
  }
  const first = focusables[0]
  const last = focusables[focusables.length - 1]
  const active = document.activeElement
  const inside = active instanceof HTMLElement && panel.contains(active)
  if (!inside || !focusables.includes(active)) {
    event.preventDefault()
    ;(event.shiftKey ? last : first).focus()
  } else if (event.shiftKey && active === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}
